import roleApi from '@/app/features/admin/roles/role-service';
import { Role } from '@/app/shared/types/models/role.interface';
import { defineStore } from 'pinia';
import { TabHeader } from '@/app/shared/types/tab-header.interface';
import { PaginatedResponse } from '@/app/shared/types/api/api-response.interface';
import { RoleDetailPage } from './role-detail.page.interface';
import { RoleAccess } from './role-access-interface';
import { RoleData } from './role-data.interface';

export const useRoleStore = defineStore({
  id: 'role-store-id',
  state: () => ({
    roleDetails: new Map<string | number, RoleDetailPage>(),
    role: {} as Role,
    roleList: {} as PaginatedResponse<Role>,
    tabs: [{ id: 0, name: 'All Roles' }] as TabHeader[],
    selectedTab: 0,
    roleAccessList: [] as Array<RoleAccess>,
    accessLevels: ['No Access', 'Read Only', 'Full Access'] as Array<string>,
    selectedAccessLevel: '' as string,
  }),
  getters: {
    getRoleById: (state) => (id: string | number) => {
      return state.roleDetails.get(id);
    },
  },
  actions: {
    async fetchRoles(
      page = 0,
      size = 20,
      sortBy = 'id',
      sortDir: 'asc' | 'desc' = 'asc'
    ) {
      const {
        data: { data },
      } = await roleApi.getRoles({ page, size, sortBy, sortDir });
      this.roleList = data;
    },

    async createRoleTab(detailPage: RoleDetailPage) {
      if (detailPage.isNew) {
        detailPage.roleData = {
          name: '',
          clientId: 0,
          ssoDefaultRole: false,
          accessRights: [],
        };
        this.roleDetails.set(detailPage.id, detailPage);
      } else {
        const resp = await roleApi.getRoleById(detailPage.id);
        detailPage.roleData = resp.data.data;
        this.roleDetails.set(detailPage.id, detailPage);
      }
      const tabIndex = this.tabs.findIndex(
        (tab: TabHeader) => tab.name === detailPage.name
      );
      if (tabIndex === -1) {
        this.tabs.push({
          id: detailPage.id,
          name: detailPage.name,
          autoLocked: false,
        });
        this.selectedTab = this.tabs.length - 1;
      } else {
        this.selectedTab = tabIndex;
      }
    },

    removeTab(selectedId: number) {
      const fIndex = this.tabs.findIndex((tab: any) => tab.id === selectedId);
      fIndex > 0 && this.tabs.splice(fIndex, 1);
    },

    closeTab(tabId: number | string) {
      const tabIndex = this.tabs.findIndex((tab) => tab.id === tabId);
      if (tabIndex !== -1 && tabIndex !== 0) {
        this.tabs.splice(tabIndex, 1);
        this.roleDetails.delete(tabId);
        if (this.selectedTab >= this.tabs.length) {
          this.selectedTab = this.tabs.length - 1;
        }
      }
    },

    selectTab(index: number) {
      this.selectedTab = index;
    },

    async deleteRole(id: number | string) {
      await roleApi.deleteRole(id);
      await this.fetchRoles();
    },

    async createRole(roleData: RoleData) {
      const roleToCreate = {
        ...roleData,
        clientId: roleData.clientId ?? 0,
        ssoDefaultRole: roleData.ssoDefaultRole ?? false,
      };
      const response = await roleApi.createRole(roleToCreate as any);
      await this.fetchRoles();
      return response.data.data;
    },

    async updateRole(id: number | string, roleData: any) {
      const response = await roleApi.updateRole(id, roleData);
      const detailPage = this.roleDetails.get(id);
      if (detailPage) {
        detailPage.roleData = response.data.data;
      }
      await this.fetchRoles();
      return response.data.data;
    },

    async getRoleAccessList() {
      if (this.roleAccessList.length === 0) {
        const {
          data: { data },
        } = await roleApi.getRoleAccessList();
        this.roleAccessList = data;
      }
      return this.roleAccessList;
    },
  },
});
